import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { FaSyncAlt, FaTable, FaThLarge, FaFilePdf } from 'react-icons/fa';
import { useImmer } from 'use-immer';
import { Autocomplete, TextField, Chip, Button, Typography } from '@mui/material';
import { toast } from 'react-hot-toast';
import './smps-dashboard.scss';

// Components
import SMPSStatCards from './components/SMPSStatCards';
import SMPSCharts from './components/SMPSCharts';
import SMPSAlertPanel from './components/SMPSAlertPanel';
import SMPSDeviceTable from './components/SMPSDeviceTable';
import SMPSDeviceCards from './components/SMPSDeviceCards';
import SMPSAssetModal from './components/SMPSAssetModal';
import { AutocompleteStationListPop } from '../AutocompleteStationListPop/AutocompleteStationListPop';

// Types and Constants
import { SMPSDevice, SMPSAlert, ViewMode, LoadTrend } from './utils/smpsTypes';
import { calculateStats } from './utils/smpsConstants';
import { TransformedSMPSResponse } from './utils/smpsApiTypes';

// Utils
import { getParsedLocalStorage } from '../../Utils/storage-utils';
import { extractParsedDataFromPermissions } from '../../Utils/Utils';

// API Service (Conventional axios method)
import { fetchSMPSEventLogs } from './utils/smpsApiService';

// PDF Export
import { exportDevicesToPdf } from './utils/smpsPdfExport';

interface FilterState {
  zone: any | null;
  division: any | null;
  station: any | null;
}

const SMPSDashboard: React.FC = () => {
  // State
  const [activeTab, setActiveTab] = useState<ViewMode>('table');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<SMPSDevice | null>(null);

  // API data state
  const [apiData, setApiData] = useState<TransformedSMPSResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Filter state for Zone/Division/Station
  const [filters, setFilters] = useImmer<FilterState>({
    zone: null,
    division: null,
    station: null,
  });

  const [appliedFilters, setAppliedFilters] = useImmer<FilterState>({
    zone: null,
    division: null,
    station: null,
  });

  // Ref to track latest applied filters for stable loadData identity
  const appliedFiltersRef = useRef<FilterState>(appliedFilters);
  appliedFiltersRef.current = appliedFilters;

  // Get permissions data
  const user_permissions = getParsedLocalStorage('permissions', null);
  const parsedPermissions = useMemo(() => {
    return extractParsedDataFromPermissions(user_permissions);
  }, [user_permissions]);

  const { zones = [], divisions = [], stations: permissionStations = [] } = parsedPermissions || {};

  // Filtered divisions based on selected zones
  const filteredDivisions = useMemo(() => {
    if (!filters.zone) return divisions;
    return divisions.filter((div: any) =>
      div.zone?.id === filters.zone.id
    );
  }, [filters.zone, divisions]);

  // Filtered stations based on selected divisions
  const filteredPermissionStations = useMemo(() => {
    if (!filters.division) {
      if (!filters.zone) return permissionStations;
      return permissionStations.filter((stn: any) =>
        stn.zone?.id === filters.zone.id
      );
    }
    return permissionStations.filter((stn: any) =>
      stn.division?.id === filters.division.id
    );
  }, [filters.zone, filters.division, permissionStations]);

  // Filter handlers
  const handleZoneChange = useCallback((_: any, newValue: any | null) => {
    setFilters((draft) => {
      draft.zone = newValue;
      // Clear dependent filters when zone changes
      draft.division = null;
      draft.station = null;
    });
  }, [setFilters]);

  const handleDivisionChange = useCallback((_: any, newValue: any | null) => {
    setFilters((draft) => {
      draft.division = newValue;
      // Clear station filter when division changes
      draft.station = null;
    });
  }, [setFilters]);

  const handleStationChange = useCallback((_: any, newValue: any | null) => {
    setFilters((draft) => {
      draft.station = newValue;
    });
  }, [setFilters]);



  // Load data function using conventional axios method
  const loadData = useCallback(async (showLoading = true, overrideFilters?: FilterState) => {
    if (showLoading) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);

    const activeFilters = overrideFilters || appliedFiltersRef.current;

    try {
      const data = await fetchSMPSEventLogs({
        page: 0,
        size: 200,
        stn: activeFilters?.station?.id,
      });
      setApiData(data);
    } catch (err: any) {
      console.error('Error loading SMPS data:', err);
      setError(err.message || 'Failed to load SMPS data');
      toast.error('Failed to load SMPS event logs');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    pollingIntervalRef.current = setInterval(() => {
      if (!isLoading && !isRefreshing) {
        loadData(false);
      }
    }, 30000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [loadData, isLoading, isRefreshing]);

  // Refetch function for manual refresh
  const refetch = useCallback(() => {
    loadData(false);
  }, [loadData]);

  const handleApplyFilters = useCallback(() => {
    if (!filters.station) {
      toast.error('All 3 fields (Zone, Division, Station) need to be selected.');
      return;
    }
    const newAppliedFilters = {
      zone: filters.zone,
      division: filters.division,
      station: filters.station,
    };
    setAppliedFilters(newAppliedFilters);
    // Trigger data load with the latest filters
    loadData(true, newAppliedFilters);
  }, [filters, setAppliedFilters, loadData]);

  const handleClearFilters = useCallback(() => {
    const clearedFilters = {
      zone: null,
      division: null,
      station: null,
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    // Reset data to full list
    loadData(true, clearedFilters);
  }, [setFilters, setAppliedFilters, loadData]);

  // Use real API data - no mock data fallback
  const devices = useMemo(() => {
    return apiData?.devices || [];
  }, [apiData?.devices]);

  const alerts = useMemo(() => {
    return apiData?.alerts || [];
  }, [apiData?.alerts]);

  const stationStats = useMemo(() => {
    return apiData?.stationStats || [];
  }, [apiData?.stationStats]);

  // Filter devices by applied Zone/Division/Station filters
  // Since the user is handling API filtering, we just use the devices from API
  const filteredDevices = useMemo(() => {
    return devices;
  }, [devices]);

  // Calculate stats based on filtered devices
  const stats = useMemo(() => {
    if (apiData?.stats && !appliedFilters.station && !appliedFilters.division && !appliedFilters.zone) {
      return apiData.stats;
    }
    return calculateStats(filteredDevices);
  }, [filteredDevices, apiData?.stats, appliedFilters]);

  // Filter station stats based on applied filters
  const filteredStationStats = useMemo(() => {
    if (!appliedFilters.station && !appliedFilters.division && !appliedFilters.zone) {
      return stationStats;
    }
    // Filter stationStats to only show stations in filteredDevices
    const activeStations = new Set(filteredDevices.map(d => d.station));
    return stationStats.filter((s) => activeStations.has(s.station));
  }, [stationStats, filteredDevices, appliedFilters]);

  // Generate load trend from current data
  const loadTrend: LoadTrend[] = useMemo(() => {
    const avgLoad = stats.totalLoad / Math.max(1, stats.totalDevices - stats.offline);
    const maxLoad = 100; // Assuming 100A max per unit
    const currentLoadPercent = Math.round((avgLoad / maxLoad) * 100);

    const clamp = (val: number) => Math.max(0, Math.min(100, val));

    return [
      { time: '00:00', loadPercent: clamp(currentLoadPercent - 5) },
      { time: '04:00', loadPercent: clamp(currentLoadPercent - 10) },
      { time: '08:00', loadPercent: clamp(currentLoadPercent + 5) },
      { time: '12:00', loadPercent: clamp(currentLoadPercent + 10) },
      { time: '16:00', loadPercent: clamp(currentLoadPercent + 5) },
      { time: '20:00', loadPercent: clamp(currentLoadPercent) },
      { time: 'Now', loadPercent: currentLoadPercent },
    ];
  }, [stats.totalLoad, stats.totalDevices, stats.offline]);

  // Handlers
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleExportPdf = useCallback(() => {
    if (filteredDevices.length === 0) {
      toast.error('No data to export');
      return;
    }
    try {
      exportDevicesToPdf(filteredDevices, stats, filteredStationStats);
      toast.success('PDF exported successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF');
    }
  }, [filteredDevices, stats, filteredStationStats]);

  const handleDeviceClick = useCallback((device: SMPSDevice) => {
    setSelectedDevice(device);
    setIsModalOpen(true);
  }, []);

  const handleAlertClick = useCallback((alert: SMPSAlert) => {
    const device = devices.find((d) => d.id === alert.deviceId);
    if (device) {
      setSelectedDevice(device);
      setIsModalOpen(true);
    }
  }, [devices]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedDevice(null);
  }, []);

  console.log(filteredDevices, "filteredDevices")

  // Loading state
  if (isLoading) {
    return (
      <div className="smps-dashboard">
        <header className="smps-header">
          <div className="logo-container">
            <div className="smps-logo">⚡</div>
            <div className="smps-title">
              <h1>Power Supply Monitoring System</h1>
              <div className="smps-subtitle">
                Real-time monitoring of SMPS/DC power systems
              </div>
            </div>
          </div>
          <div className="system-status">
            <div className="status-indicator">
              <div className="status-dot" style={{ backgroundColor: '#ffc107' }}></div>
              <span>Loading...</span>
            </div>
          </div>
        </header>
        <div className="loading-container" style={{ padding: '100px', textAlign: 'center' }}>
          <div className="loading-spinner" style={{ fontSize: '24px', color: '#1a5276' }}>
            Loading power system data...
          </div>
        </div>
      </div>
    );
  }

  // Error state - show with mock data
  if (error && (!devices || devices.length === 0)) {
    return (
      <div className="smps-dashboard">
        <header className="smps-header">
          <div className="logo-container">
            <div className="smps-logo">⚡</div>
            <div className="smps-title">
              <h1>Power Supply Monitoring System</h1>
              <div className="smps-subtitle">
                Real-time monitoring of SMPS/DC power systems
              </div>
            </div>
          </div>
          <div className="system-status">
            <div className="status-indicator">
              <div className="status-dot" style={{ backgroundColor: '#dc3545' }}></div>
              <span>Error</span>
            </div>
          </div>
        </header>
        <div className="error-container" style={{ padding: '100px', textAlign: 'center' }}>
          <div style={{ fontSize: '18px', color: '#dc3545', marginBottom: '20px' }}>
            Failed to load power system data
          </div>
          <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px' }}>
            {error || 'Please check your network connection'}
          </div>
          <button
            onClick={handleRefresh}
            style={{
              padding: '10px 20px',
              backgroundColor: '#1a5276',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            <FaSyncAlt style={{ marginRight: '8px' }} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="smps-dashboard">
      {/* Compact Header */}
      <header className="smps-header">
        <div className="logo-container">
          <div className="smps-logo">⚡</div>
          <div className="smps-title">
            <h1>Power Supply Monitoring System</h1>
            <div className="smps-subtitle">
              Real-time monitoring of SMPS/DC power systems
            </div>
          </div>
        </div>
        <div className="system-status">
          <div className="status-indicator">
            <div className="status-dot" style={{ backgroundColor: isRefreshing ? '#ffc107' : '#27ae60' }}></div>
            <span>{isRefreshing ? 'Refreshing...' : 'Operational'}</span>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <SMPSStatCards stats={stats} />

      {/* Zone/Division/Station Filter Section */}
      <div className="smps-filter-section">
        <Typography variant="subtitle2" className="filter-title">
          Filter by Location
        </Typography>
        <div className="filter-row">
          <Autocomplete
            size="small"
            options={zones}
            getOptionLabel={(option: any) => option.value || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={filters.zone}
            onChange={handleZoneChange}
            renderInput={(params) => (
              <TextField {...params} label="Zone" placeholder="Select Zone" />
            )}
            className="filter-autocomplete"
          />
          <Autocomplete
            size="small"
            options={filteredDivisions}
            getOptionLabel={(option: any) => option.value || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={filters.division}
            onChange={handleDivisionChange}
            renderInput={(params) => (
              <TextField {...params} label="Division" placeholder="Select Division" />
            )}
            className="filter-autocomplete"
          />
          <Autocomplete
            size="small"
            options={filteredPermissionStations}
            getOptionLabel={(option: any) => option.value || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={filters.station}
            onChange={handleStationChange}
            renderInput={(params) => (
              <TextField {...params} label="Station" placeholder="Select Station" />
            )}
            // PopperComponent={AutocompleteStationListPop}
            className="filter-autocomplete"
          />
          <div className="filter-actions">
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyFilters}
              className="apply-btn"
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              className="clear-btn"
            >
              Clear
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
              startIcon={<FaSyncAlt className={isRefreshing ? 'spin' : ''} />}
              className="refresh-btn"
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleExportPdf}
              disabled={isRefreshing || filteredDevices.length === 0}
              startIcon={<FaFilePdf />}
              color="error"
              className="export-btn"
            >
              Export PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        <SMPSCharts
          stationStats={filteredStationStats}
          loadTrend={loadTrend}
          devices={filteredDevices}
        />
      </div>

      {/* Alerts Section */}
      <div className="alerts-health-section">
        <SMPSAlertPanel alerts={alerts} onAlertClick={handleAlertClick} />
      </div>

      {/* Tabs Section - Table/Card View */}
      <div className="tabs-section">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 'table' ? 'active' : ''}`}
            onClick={() => setActiveTab('table')}
          >
            <FaTable />
            Table View
          </button>
          <button
            className={`tab-button ${activeTab === 'card' ? 'active' : ''}`}
            onClick={() => setActiveTab('card')}
          >
            <FaThLarge />
            Card View
          </button>
        </div>

        {/* Table View */}
        <div className={`tab-content ${activeTab === 'table' ? 'active' : ''}`}>
          <SMPSDeviceTable devices={filteredDevices} onDeviceClick={handleDeviceClick} />
        </div>

        {/* Card View */}
        <div className={`tab-content ${activeTab === 'card' ? 'active' : ''}`}>
          <SMPSDeviceCards devices={filteredDevices} onDeviceClick={handleDeviceClick} />
        </div>
      </div>

      {/* Asset Details Modal */}
      <SMPSAssetModal
        isOpen={isModalOpen}
        deviceData={selectedDevice}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default SMPSDashboard;
